// History across successive runs — Hadi's "what did we do last week" jog:
// every change grouped by the run that applied it, newest run open.

import { useState } from 'react';
import type { RoadmapEvent } from '../../services/roadmapReports';
import { changedActionRefs, groupEventsByRun, meaningfulChanges } from './roadmapViewUtils';
import type { ModalSection } from './TimelineChart';
import styles from './RoadmapV2.module.css';

interface Props {
  events: RoadmapEvent[];
  /** Open the initiative pop-up; with an action key, scoped to that sub-action. */
  onSelect: (key: string, section?: ModalSection, action?: string) => void;
  /** The run reports, so each run opens with its own plain summary. */
  reports?: { id: string; summary?: string | null }[];
}

export function RunHistoryPanel({ events, onSelect, reports = [] }: Props) {
  // Count and list only events with something to read (see meaningfulChanges).
  const runs = groupEventsByRun(events.filter((e) => meaningfulChanges(e).length > 0));
  const [open, setOpen] = useState<Set<string>>(() => new Set(runs[0] ? [runs[0].reportId ?? ''] : []));
  if (runs.length === 0) return null;
  const summaryOf = (id: string | null) => (id ? reports.find((r) => r.id === id)?.summary ?? null : null);
  const toggle = (id: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <section className={styles.panel} aria-label="Changes by run">
      <div className={styles.panelHead}>
        <h2>What changed, run by run</h2>
        <span className={styles.meta}>
          {runs.length} run{runs.length === 1 ? '' : 's'} · {runs.reduce((n, r) => n + r.events.length, 0)} changes · newest first
        </span>
      </div>
      {runs.map((run) => {
        const id = run.reportId ?? '';
        const isOpen = open.has(id);
        const created = run.events.filter((e) => e.kind === 'created').length;
        return (
          <div key={id || 'none'} className={styles.run}>
            <div
              className={styles.runHead}
              onClick={() => toggle(id)}
              role="button"
              aria-expanded={isOpen}
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggle(id); }}
            >
              <span>{isOpen ? '▾' : '▸'}</span>
              <strong>{run.reportTitle}</strong>
              <span className={styles.runDate}>{run.reportDate ?? ''}</span>
              <span className={styles.meta}>
                {run.events.length - created} updated · {created} new
              </span>
            </div>
            {isOpen && (
              <>
                {summaryOf(run.reportId) && <p className={styles.runSummary}>{summaryOf(run.reportId)}</p>}
                <ul className={styles.runEvents}>
                  {run.events.map((ev) => {
                    const lines = meaningfulChanges(ev);
                    if (lines.length === 0) return null;
                    return (
                      <li key={ev.id}>
                        <button type="button" className={`${styles.linkBtn} ${styles.evTitle}`} onClick={() => onSelect(ev.initiative_key)}>
                          {ev.title}
                        </button>
                        <ul className={styles.evLines}>
                          {lines.map((line, n) => (
                            <li key={n} className={styles.evKind}>{line}</li>
                          ))}
                        </ul>
                        {/* The sub-actions this run added or moved, each a button to its pop-up. */}
                        {changedActionRefs(ev).length > 0 && (
                          <div className={styles.subActionRefs} data-testid={`ev-actions-${ev.id}`}>
                            {changedActionRefs(ev).map((r) => (
                              <button
                                key={r.key}
                                type="button"
                                className={styles.subActionRef}
                                onClick={() => onSelect(ev.initiative_key, 'dates', r.key)}
                                title="Open this sub-action"
                                data-action="roadmap_action_open"
                                data-target-initiative-key={ev.initiative_key}
                                data-target-action-key={r.key}
                              >
                                {r.title}
                              </button>
                            ))}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </>
            )}
          </div>
        );
      })}
    </section>
  );
}
